import React, { useState } from "react";
import { Logo } from "../components/tabon/Logo";
import { api, formatError } from "../lib/api";
import { toast } from "sonner";
import {
  ArrowRight, X, MessageCircle, Star, Sparkles,
  ChevronDown, Smartphone, MapPin, Phone, Mail,
} from "lucide-react";

// "Venue Login" routes to the dashboard on the `dashboard.tabon.app` subdomain.
const VENUE_LOGIN_HREF = "https://dashboard.tabon.app";
const VENUE_TYPES = ["Bar", "Restaurant", "Café", "Lounge", "Club", "QSR", "Other"];

export default function Landing() {
  const [showDemo, setShowDemo] = useState(false);

  const scrollTo = (id) => () => {
    const el = document.getElementById(id);
    if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
  };

  const openDemo = () => setShowDemo(true);

  return (
    <div className="min-h-screen tb-hero-gradient relative overflow-hidden">
      <div className="absolute inset-0 tb-grid-bg opacity-30 pointer-events-none" />

      {/* Nav */}
      <header className="relative flex items-center justify-between px-6 md:px-12 py-5">
        <Logo size="md" />
        <nav className="hidden md:flex items-center gap-6">
          <NavLink onClick={scrollTo("features")} testid="nav-features">Features</NavLink>
          <NavLink onClick={scrollTo("how")} testid="nav-how">How It Works</NavLink>
          <NavLink onClick={scrollTo("about")} testid="nav-about">About</NavLink>
          <NavLink onClick={scrollTo("contact")} testid="nav-contact">Contact</NavLink>
        </nav>
        <div className="flex items-center gap-3">
          <button
            onClick={openDemo}
            data-testid="nav-early-access"
            className="tb-glow bg-[#9D00FF] hover:bg-[#8C00E6] text-white rounded-lg px-4 py-2 text-sm font-semibold transition"
          >
            Book a Demo
          </button>
        </div>
      </header>

      {/* Hero */}
      <section className="relative px-6 md:px-12 pt-16 pb-24 max-w-6xl mx-auto">
        <div className="tb-fade-up flex flex-col items-start">
          <div className="inline-flex items-center gap-2 bg-[#9D00FF]/10 border border-[#9D00FF]/30 rounded-full px-3 py-1 text-xs text-[#D78CFF] mb-6">
            <Sparkles size={12} /> Built for Indian bars, restaurants & lounges
          </div>
          <h1 className="text-5xl md:text-7xl lg:text-8xl font-black tracking-tighter leading-[0.95]">
            Turn Every Visit<br />
            <span className="text-[#9D00FF]">Into The Next One.</span>
          </h1>
          <p className="text-lg md:text-xl text-[#A0A0A0] mt-6 max-w-2xl leading-relaxed">
            A loyalty and engagement platform built for Indian bars, restaurants, cafés, and lounges.
            Your customers earn points, get rewarded, and receive personalised WhatsApp messages —
            automatically, with no app download and no change to how your venue already works.
          </p>
          <div className="flex flex-wrap items-center gap-3 mt-8">
            <button
              onClick={openDemo}
              data-testid="hero-join-pilot"
              className="tb-glow bg-[#9D00FF] hover:bg-[#8C00E6] text-white rounded-lg px-6 py-3 font-semibold flex items-center gap-2 transition"
            >
              Book a Demo <ArrowRight size={18} />
            </button>
            <button
              onClick={scrollTo("how")}
              data-testid="hero-how-it-works"
              className="rounded-lg px-6 py-3 border border-white/15 text-white hover:bg-white/5 transition font-medium"
            >
              See How It Works
            </button>
          </div>
          <p className="text-sm text-[#A0A0A0] mt-4" data-testid="hero-pilot-note">
            No commitment required · Live in 48 hours
          </p>
        </div>
      </section>

      {/* Credibility Bar */}
      <section
        data-testid="credibility-bar"
        className="relative border-y border-white/10 bg-white/[0.02]"
      >
        <div className="max-w-6xl mx-auto px-6 md:px-12 py-4 text-center text-sm text-[#A0A0A0]">
          Built specifically for India's F&B industry · Bars · Restaurants · Cafés · Lounges · Clubs · QSRs
        </div>
      </section>

      {/* Problem */}
      <section id="problem" className="relative px-6 md:px-12 py-20 max-w-6xl mx-auto">
        <div className="max-w-3xl mb-12">
          <h2 className="text-3xl md:text-5xl font-black tracking-tight leading-tight">
            Most venues have no system working for them between visits.
          </h2>
          <p className="text-lg text-[#A0A0A0] mt-4">
            It's not that customers didn't have a good time. It's that nothing connected
            that good time to a reason to return.
          </p>
        </div>
        <div className="grid sm:grid-cols-2 gap-4">
          {[
            {
              title: "You don't know who walked in last night.",
              body: "Every night, customers spend money, have a good time, and leave. No name. No number. No record. The next time they visit, your staff starts from zero again.",
            },
            {
              title: "Nothing pulls them back.",
              body: "A great experience creates a memory. Without a system to act on that memory — a reward waiting, a message at the right moment — that customer's next visit goes to whoever reaches them first.",
            },
          ].map((c, i) => (
            <div
              key={i}
              data-testid={`problem-card-${i}`}
              className="bg-[#1A1A1A] border border-white/10 rounded-xl p-6 hover:border-[#9D00FF]/40 transition"
            >
              <h3 className="text-xl font-bold mb-2">{c.title}</h3>
              <p className="text-sm text-[#A0A0A0] leading-relaxed">{c.body}</p>
            </div>
          ))}
        </div>
      </section>

      {/* Features */}
      <section id="features" className="relative px-6 md:px-12 py-20 max-w-6xl mx-auto">
        <div className="max-w-3xl mb-12">
          <h2 className="text-3xl md:text-5xl font-black tracking-tight leading-tight">
            Everything your venue needs to keep customers coming back.
          </h2>
        </div>
        <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4">
          {[
            {
              icon: Smartphone,
              title: "Digital QR Menu",
              body: "A branded digital menu customers scan at the table. Update prices, add specials, and mark items as sold out instantly — no printing, no reprinting, no cost.",
            },
            {
              icon: Star,
              title: "Loyalty Programme",
              body: "Customers earn points on every visit and come back to spend them. You set the rate, the milestones, and the rewards. When a group dines together, points split automatically across the whole table — every person enrolled, not just the one who pays. The system runs itself.",
            },
            {
              icon: MessageCircle,
              title: "WhatsApp Engagement",
              body: "TabOn keeps your venue in your customers' lives between visits — automatically, on WhatsApp, in your name. Your team sends nothing manually.",
            },
            {
              icon: Sparkles,
              title: "Something No Other Platform in India Offers",
              body: "TabOn includes a feature exclusive to the platform — one that gives your customers a concrete, guaranteed reason to return to your venue specifically. We don't describe it publicly. Ask us about it when you book a demo.",
            },
          ].map((f, i) => (
            <FeatureCard key={i} icon={f.icon} title={f.title} body={f.body} />
          ))}
        </div>
      </section>

      {/* How It Works */}
      <section id="how" className="relative px-6 md:px-12 py-20 max-w-6xl mx-auto">
        <div className="max-w-3xl mb-12">
          <h2 className="text-3xl md:text-5xl font-black tracking-tight leading-tight">
            Simple for your staff. Powerful for your business.
          </h2>
          <p className="text-lg text-[#A0A0A0] mt-4">
            The only change to your existing workflow is one line at billing: the customer's
            phone number.
          </p>
        </div>
        <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4">
          {[
            {
              n: "01",
              title: "Customer bills",
              body: "Staff enter the customer's phone number at the counter. TabOn logs the visit and awards points. Under 10 seconds.",
            },
            {
              n: "02",
              title: "Customer receives WhatsApp",
              body: "A branded receipt lands on their WhatsApp immediately — points earned, current balance, your venue's name. No app, no login.",
            },
            {
              n: "03",
              title: "System runs automatically",
              body: "Milestone alerts, birthday messages, and win-back campaigns fire at the right time. No manual work from your team.",
            },
            {
              n: "04",
              title: "You see everything",
              body: "Your dashboard shows every customer, every visit, every balance. See who your regulars are, who hasn't been in a while, and what's working.",
            },
          ].map((s) => (
            <div
              key={s.n}
              className="bg-[#1A1A1A] border border-white/10 rounded-xl p-5 hover:border-[#9D00FF]/50 transition-all hover:-translate-y-0.5"
            >
              <div className="text-5xl font-black text-[#9D00FF] mb-3 tracking-tighter">{s.n}</div>
              <h3 className="font-bold text-white mb-2 text-lg">{s.title}</h3>
              <p className="text-sm text-[#A0A0A0] leading-relaxed">{s.body}</p>
            </div>
          ))}
        </div>
      </section>

      {/* FAQ */}
      <section id="faq" className="relative px-6 md:px-12 py-20 max-w-6xl mx-auto">
        <div className="max-w-3xl mb-12">
          <h2 className="text-3xl md:text-5xl font-black tracking-tight leading-tight">
            Questions we hear from every venue
          </h2>
        </div>
        <div className="flex flex-col gap-3 max-w-4xl">
          {[
            {
              q: "Does my staff need training?",
              a: "If your staff can look up a table number, they can use TabOn. The entire staff interface is built for the billing counter — it takes under 30 seconds per transaction.",
              testid: "faq-0",
            },
            {
              q: "Does TabOn replace my existing billing or POS system?",
              a: "No. TabOn works alongside whatever you already use for billing. The only addition is entering the customer's phone number at the counter.",
              testid: "faq-1",
            },
            {
              q: "How quickly will we see results?",
              a: "Customers with an active points balance return specifically because of it. Most venues see TabOn-driven return visits within the first two weeks.",
              testid: "faq-2",
            },
            {
              q: "Is my customer data secure?",
              a: "All data is stored on Indian servers, encrypted, and never shared with third parties. Full details in our Privacy Policy.",
              testid: "faq-3",
            },
          ].map((item) => (
            <FaqItem key={item.testid} q={item.q} a={item.a} testid={item.testid} />
          ))}
        </div>
      </section>

      {/* About */}
      <section id="about" className="relative px-6 md:px-12 py-20 max-w-6xl mx-auto">
        <div className="max-w-3xl mb-12">
          <h2 className="text-3xl md:text-5xl font-black tracking-tight leading-tight">
            Built in India, for India's F&B industry.
          </h2>
        </div>

        <div className="grid md:grid-cols-2 gap-8 max-w-4xl">
          <div className="flex flex-col gap-5">
            <p className="text-[#A0A0A0] leading-relaxed">
              Manan Sarawgi founded TabOn after noticing something simple about human behaviour:
              people naturally return to places where they have something left.
            </p>
            <p className="text-[#A0A0A0] leading-relaxed">
              He saw an opportunity to build around that instinct — to give venues a system that
              ensures every customer always has a reason to come back. Something earned, something
              waiting, something that belongs to them at your venue specifically.
            </p>
            <p className="text-[#A0A0A0] leading-relaxed">
              TabOn is his attempt to give India's bars, restaurants, cafés, and lounges the digital
              tools to keep pace with how the world — and customer expectations — are changing.
            </p>
            <p className="text-sm text-[#6B6B6B]">
              TabOn Labs is based in Udaipur, Rajasthan and is registered as a company under the
              Government of India.
            </p>
          </div>

          <div className="bg-[#1A1A1A] border border-white/10 rounded-xl p-6 flex flex-col gap-4 h-fit">
            <p className="text-sm font-bold text-white">
              Manan Sarawgi
            </p>
            <p className="text-sm text-[#A0A0A0]">
              Founder, TabOn Labs
            </p>
            <div className="border-t border-white/10 pt-4 flex flex-col gap-3">
              <a
                href="mailto:cheers@tabon.app"
                className="flex items-center gap-3 text-sm text-[#A0A0A0] hover:text-white transition"
              >
                <Mail size={15} className="text-[#9D00FF] shrink-0" />
                cheers@tabon.app
              </a>
              <a
                href="tel:+918920074695"
                className="flex items-center gap-3 text-sm text-[#A0A0A0] hover:text-white transition"
              >
                <Phone size={15} className="text-[#9D00FF] shrink-0" />
                +91 89200 74695
              </a>
              <a
                href="https://wa.me/918920074695"
                target="_blank"
                rel="noopener noreferrer"
                className="flex items-center gap-3 text-sm text-[#A0A0A0] hover:text-white transition"
              >
                <MessageCircle size={15} className="text-[#9D00FF] shrink-0" />
                WhatsApp
              </a>
              <div className="flex items-center gap-3 text-sm text-[#A0A0A0]">
                <MapPin size={15} className="text-[#9D00FF] shrink-0" />
                Udaipur, Rajasthan
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Contact */}
      <section id="contact" className="relative px-6 md:px-12 py-20 max-w-6xl mx-auto">
        <div className="max-w-3xl mb-10">
          <h2 className="text-3xl md:text-5xl font-black tracking-tight leading-tight">
            Talk to us.
          </h2>
          <p className="text-lg text-[#A0A0A0] mt-4">
            We respond the same day.
          </p>
        </div>

        <div className="grid sm:grid-cols-3 gap-4 max-w-2xl">
          <a
            href="mailto:cheers@tabon.app"
            className="bg-[#1A1A1A] border border-white/10 rounded-xl p-5 hover:border-[#9D00FF]/50 transition flex flex-col gap-2"
          >
            <Mail size={20} className="text-[#9D00FF]" />
            <p className="text-xs text-[#A0A0A0] uppercase tracking-wider font-bold">
              Email
            </p>
            <p className="text-sm text-white">
              cheers@tabon.app
            </p>
          </a>

          <a
            href="tel:+918920074695"
            className="bg-[#1A1A1A] border border-white/10 rounded-xl p-5 hover:border-[#9D00FF]/50 transition flex flex-col gap-2"
          >
            <Phone size={20} className="text-[#9D00FF]" />
            <p className="text-xs text-[#A0A0A0] uppercase tracking-wider font-bold">
              Phone
            </p>
            <p className="text-sm text-white">
              +91 89200 74695
            </p>
          </a>

          <a
            href="https://wa.me/918920074695"
            target="_blank"
            rel="noopener noreferrer"
            className="bg-[#1A1A1A] border border-white/10 rounded-xl p-5 hover:border-[#9D00FF]/50 transition flex flex-col gap-2"
          >
            <MessageCircle size={20} className="text-[#9D00FF]" />
            <p className="text-xs text-[#A0A0A0] uppercase tracking-wider font-bold">
              WhatsApp
            </p>
            <p className="text-sm text-white">
              +91 89200 74695
            </p>
          </a>
        </div>
      </section>

      {/* Final CTA */}
      <section
        data-testid="final-cta"
        className="relative bg-[#2D1B4E] border-y border-white/10"
      >
        <div className="max-w-6xl mx-auto px-6 md:px-12 py-20 text-center flex flex-col items-center">
          <h2 className="text-3xl md:text-5xl font-black tracking-tight leading-tight text-white max-w-3xl">
            Ready to start turning visits into relationships?
          </h2>
          <p className="text-lg text-[#D0C8E0] mt-4 max-w-2xl">
            Book a 20-minute demo. We'll walk you through the platform, show you how it works
            for your type of venue, and have you live within 48 hours if you decide to go ahead.
          </p>
          <button
            onClick={openDemo}
            data-testid="final-cta-early-access"
            className="tb-glow bg-[#9D00FF] hover:bg-[#8C00E6] text-white rounded-lg px-6 py-3 font-semibold flex items-center gap-2 transition mt-8"
          >
            Book a Demo <ArrowRight size={18} />
          </button>
          <p className="text-xs text-[#D0C8E0] mt-4">
            No commitment required · Setup in under an hour
          </p>
        </div>
      </section>

      {/* Footer */}
      <footer className="relative border-t border-white/5">
        <div className="max-w-6xl mx-auto px-6 md:px-12 py-10 grid gap-8 md:grid-cols-3 items-start">
          <div>
            <Logo size="md" />
            <p className="text-sm text-[#A0A0A0] mt-3">Turn Every Visit Into The Next One.</p>
          </div>
          <div className="flex flex-wrap gap-x-5 gap-y-2 md:justify-center">
            <FooterLink onClick={scrollTo("features")} testid="footer-features">Features</FooterLink>
            <FooterLink onClick={scrollTo("how")} testid="footer-how">How It Works</FooterLink>
            <FooterLink onClick={scrollTo("about")} testid="footer-about">About</FooterLink>
            <FooterLink onClick={scrollTo("contact")} testid="footer-contact">Contact</FooterLink>
            <FooterLink onClick={openDemo} testid="footer-demo">Book a Demo</FooterLink>
            <a
              href={VENUE_LOGIN_HREF}
              data-testid="footer-venue-login"
              className="text-sm text-[#A0A0A0] hover:text-white transition"
            >
              Venue Login
            </a>
            <a
              href="/privacy"
              data-testid="footer-privacy"
              className="text-sm text-[#A0A0A0] hover:text-white transition"
            >
              Privacy Policy
            </a>
          </div>
          <div className="md:text-right flex flex-col gap-2">
            <a href="mailto:cheers@tabon.app" className="text-sm text-white hover:text-[#9D00FF] transition">
              cheers@tabon.app
            </a>
            <a href="tel:+918920074695" className="text-sm text-[#A0A0A0] hover:text-white transition">
              +91 89200 74695
            </a>
            <p className="text-sm text-[#A0A0A0]">
              Udaipur, Rajasthan
            </p>
          </div>
        </div>
        <div className="border-t border-white/5 py-4">
          <div className="max-w-6xl mx-auto px-6 md:px-12 text-xs text-[#A0A0A0] text-center">
            <div data-testid="footer-copyright">
              © 2026 TabOn Labs · All rights reserved
            </div>
          </div>
        </div>
      </footer>

      {showDemo && <BookDemoModal onClose={() => setShowDemo(false)} />}
    </div>
  );
}

function FeatureCard({ icon: Icon, title, body }) {
  return (
    <div className="bg-[#1A1A1A] border border-white/10 rounded-xl p-5 hover:border-[#9D00FF]/50 transition-all hover:-translate-y-0.5 flex flex-col">
      <Icon size={22} className="text-[#9D00FF] mb-3" />
      <h3 className="font-bold text-white mb-2 text-base">{title}</h3>
      <p className="text-sm text-[#A0A0A0] leading-relaxed">{body}</p>
    </div>
  );
}

function NavLink({ children, onClick, testid }) {
  return (
    <button
      onClick={onClick}
      data-testid={testid}
      className="text-sm text-[#A0A0A0] hover:text-white transition"
    >
      {children}
    </button>
  );
}

function FooterLink({ children, onClick, testid }) {
  return (
    <button
      onClick={onClick}
      data-testid={testid}
      className="text-sm text-[#A0A0A0] hover:text-white transition"
    >
      {children}
    </button>
  );
}

function FaqItem({ q, a, testid }) {
  const [open, setOpen] = useState(false);
  return (
    <div
      data-testid={testid}
      className="bg-[#1A1A1A] border border-white/10 rounded-xl overflow-hidden"
    >
      <button
        onClick={() => setOpen((v) => !v)}
        data-testid={`${testid}-toggle`}
        className="w-full flex items-center justify-between gap-4 text-left px-5 py-4 hover:bg-white/[0.02] transition"
      >
        <span className="font-semibold text-white text-base">{q}</span>
        <ChevronDown
          size={18}
          className={`text-[#A0A0A0] shrink-0 transition-transform ${open ? "rotate-180" : ""}`}
        />
      </button>
      {open && (
        <div className="px-5 pb-5 text-sm text-[#A0A0A0] leading-relaxed">{a}</div>
      )}
    </div>
  );
}

function BookDemoModal({ onClose }) {
  const [form, setForm] = useState({
    name: "", venue_name: "", phone: "", city: "", venue_type: VENUE_TYPES[0],
  });
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await api.post("/demo-requests", form);
      setDone(true);
    } catch (err) {
      toast.error(formatError(err));
    } finally {
      setLoading(false);
    }
  };

  const field = (label, key, type = "text", placeholder = "") => (
    <div>
      <label className="text-xs font-bold uppercase tracking-wider text-[#A0A0A0] mb-1.5 block">
        {label}
      </label>
      <input
        data-testid={`demo-${key}`}
        type={type}
        required
        value={form[key]}
        onChange={(e) => setForm({ ...form, [key]: e.target.value })}
        placeholder={placeholder}
        className="w-full bg-[#0A0A0A] border border-white/10 rounded-lg px-4 py-3 min-h-[44px] text-white placeholder:text-[#6B6B6B] focus:border-[#9D00FF] focus:ring-1 focus:ring-[#9D00FF]/40"
      />
    </div>
  );

  return (
    <div className="fixed inset-0 z-50 bg-black/70 backdrop-blur-sm flex items-center justify-center p-4">
      <div className="bg-[#1A1A1A] border border-white/10 rounded-xl w-full max-w-md p-6 tb-fade-up">
        <div className="flex items-start justify-between mb-4 gap-3">
          <div>
            <h2 className="text-xl font-bold">Book a Demo</h2>
            <p className="text-sm text-[#A0A0A0] mt-1">
              Tell us about your venue and we'll be in touch within 24 hours.
            </p>
          </div>
          <button onClick={onClose} data-testid="demo-close" className="p-2 text-[#A0A0A0] hover:text-white shrink-0">
            <X size={18} />
          </button>
        </div>
        {done ? (
          <div className="py-6 text-center">
            <div className="w-14 h-14 mx-auto rounded-full bg-[#9D00FF]/15 border border-[#9D00FF]/40 flex items-center justify-center mb-3">
              <Sparkles size={22} className="text-[#9D00FF]" />
            </div>
            <h3 className="text-lg font-bold text-white mb-1">Thank you, {form.name.split(" ")[0] || "there"}.</h3>
            <p className="text-sm text-[#A0A0A0]">
              Our team will reach out to <span className="text-white">{form.phone}</span> within 24 hours.
            </p>
            <button
              onClick={onClose}
              data-testid="demo-close-after"
              className="mt-5 tb-glow bg-[#9D00FF] hover:bg-[#8C00E6] text-white rounded-lg px-4 py-2.5 font-semibold"
            >
              Close
            </button>
          </div>
        ) : (
          <form onSubmit={submit} className="flex flex-col gap-4">
            {field("Your Name", "name", "text", "Full name")}
            {field("Venue Name", "venue_name", "text", "e.g. The Copper Lounge")}
            <div>
              <label className="text-xs font-bold uppercase tracking-wider text-[#A0A0A0] mb-1.5 block">
                Type of Venue
              </label>
              <select
                data-testid="demo-venue_type"
                value={form.venue_type}
                onChange={(e) => setForm({ ...form, venue_type: e.target.value })}
                className="w-full bg-[#0A0A0A] border border-white/10 rounded-lg px-4 py-3 min-h-[44px] text-white focus:border-[#9D00FF] focus:ring-1 focus:ring-[#9D00FF]/40"
              >
                {VENUE_TYPES.map((t) => <option key={t}>{t}</option>)}
              </select>
            </div>
            {field("Phone Number", "phone", "tel", "+91…")}
            {field("City", "city", "text", "e.g. Mumbai")}
            <button
              type="submit"
              disabled={loading}
              data-testid="demo-submit"
              className="tb-glow bg-[#9D00FF] hover:bg-[#8C00E6] disabled:opacity-60 text-white font-semibold rounded-lg py-3 min-h-[44px] transition-all"
            >
              {loading ? "Sending…" : "Request My Pilot Spot"}
            </button>
          </form>
        )}
      </div>
    </div>
  );
}
